import { ConfirmDialog, Dialog } from "components/dialog";
import Button from "components/form/button";
import TextField from "components/form/text-field";
import { Form } from "formsy-react";
import { $, merge } from "glamor";
import { formatMessage } from "lib/globalize-util";
import React from "react";
import { theme } from "styles/theme";
import { DATATEST } from "constants/integration";
import { WindowAnalytics } from "helpers/window-analytics";
import { MAX_ATTRIBUTE_NAME_LENGTH } from "helpers/utils/constants";

interface IProps {
    onSaveDoorGrouping: (groupName: string) => void;
    onCancel?: () => void;
    onCancelConfirmDialog?: () => void;
    onDeleteDoorGrouping?: (id: string) => void;
    attributeKey?: string;
    doorGroupingOptions: any[];
    doorGroupingNameError?: string;
    isDialogOpen?: boolean;
    isConfirmDialogOpen?: boolean;
}

const ALPHA_REGEX = /^[A-Za-z0-9\s]*$/i;

/**
 * Dumb component for create/edit door attribute dialog.
 */
export const CreateDoorAttributesDialogComponent: React.FunctionComponent<IProps> = ({
    onCancel,
    onCancelConfirmDialog,
    onSaveDoorGrouping,
    attributeKey,
    doorGroupingOptions,
    doorGroupingNameError,
    isDialogOpen,
    isConfirmDialogOpen,
    onDeleteDoorGrouping,
}) => {
    const [doorGroupingName, setDoorGroupingName] = React.useState<string>(
        attributeKey ? doorGroupingOptions.find((group) => group.id === attributeKey)?.name : ""
    );
    const [isSubmitted, setIsSubmitted] = React.useState<boolean>(false);
    const handleDeleteDoorGrouping = React.useCallback(() => {
        onDeleteDoorGrouping(attributeKey);
        WindowAnalytics.track("Delete custom attribute", {});
    }, [onDeleteDoorGrouping, attributeKey]);

    const dialogStyle = React.useMemo(() => {
        return merge(
            {
                width: "100% !important",
            },
            $(" .btnCancel", {
                marginRight: "10px !important",
            }),
            $(" .doorGroupingBody", {
                height: "100px",
            }),
            $(" .doorGroupingNameField > div > div", {
                color: `${theme.palette.darkerGrey} !important`,
                fontWeight: "700 !important",
            })
        );
    }, []);

    const isAlphanumeric = (state: unknown, value): boolean => {
        return ALPHA_REGEX.test(value);
    };

    const isSaveButtonDisabled = React.useMemo(() => {
        return !doorGroupingName.length || doorGroupingName.trim() === "" || !ALPHA_REGEX.test(doorGroupingName);
    }, [doorGroupingName]);

    const handleSaveDoorGrouping = () => {
        if (!isSaveButtonDisabled) {
            setIsSubmitted(true);
            onSaveDoorGrouping(doorGroupingName);
            WindowAnalytics.track(attributeKey ? "Rename custom attribute" : "Create Custom Attribute", {});
        }
    };

    const handleChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsSubmitted(false);
        setDoorGroupingName(event.target.value);
    };

    return (
        <>
            <Dialog
                open={isDialogOpen}
                onRequestClose={onCancel}
                title={formatMessage(attributeKey ? "Edit attribute" : "Add new attribute")}
                contentClassName={`${dialogStyle}`}
                actions={[
                    <Button key="cancel" label={formatMessage("Cancel")} onClick={onCancel} className="btnCancel" />,
                    <Button
                        disabled={isSaveButtonDisabled}
                        key="doorGroupingBtnSave"
                        label={formatMessage("Save")}
                        onClick={handleSaveDoorGrouping}
                        data-test={DATATEST.ASSORTMENTS.GROUPING.ATTRIBUTES.SAVE}
                        primary={true}
                    />,
                ]}
            >
                <div className="doorGroupingBody">
                    <Form>
                        <div>
                            <p style={{ fontSize: "12px", color: "#585858" }} className="message">
                                {formatMessage("Manually add a new attribute to this assortment.")}
                            </p>
                        </div>
                        <div className={"doorGroupingNameField"}>
                            <TextField
                                large={false}
                                fullWidth={true}
                                label={formatMessage("Attribute name")}
                                placeholder={formatMessage("Please enter")}
                                onChange={handleChangeName}
                                name="doorGroupingName"
                                maxLength={MAX_ATTRIBUTE_NAME_LENGTH}
                                autoFocus
                                dataTest={DATATEST.ASSORTMENTS.GROUPING.ATTRIBUTES.INPUT_NAME}
                                validateOnInput={true}
                                validations={{ isValid: isAlphanumeric }}
                                validationError={formatMessage("Must be alphanumeric.")}
                                defaultValue={doorGroupingName}
                                errorText={isSubmitted && doorGroupingNameError}
                                errorStyle={{ marginTop: "8px" }}
                                onEnter={handleSaveDoorGrouping}
                            />
                        </div>
                    </Form>
                </div>
            </Dialog>
            {isConfirmDialogOpen && (
                <ConfirmDialog
                    title=""
                    open={isConfirmDialogOpen}
                    onConfirm={handleDeleteDoorGrouping}
                    onCancel={onCancelConfirmDialog}
                >
                    <p>{formatMessage("Are you sure you want to delete this door group?")}</p>
                </ConfirmDialog>
            )}
        </>
    );
};
